import React from 'react';
import { ExperienceAppTile } from './ExperienceAppTile';
import type { AppTileData } from './ExperienceAppTile';
import { Star, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import './ExperienceAppTile.css';

export interface ExperienceAppGridProps {
	apps: AppTileData[];
	featured?: boolean;
	favorites?: Set<string>;
	gridRef?: React.RefObject<HTMLDivElement | null>;
	onNavigate?: (path: string) => void;
	onToggleFavorite?: (path: string) => void;
}

export function ExperienceAppGrid({
	apps,
	featured,
	favorites,
	gridRef,
	onNavigate,
	onToggleFavorite,
}: ExperienceAppGridProps) {
	return (
		<div ref={gridRef} className={`exp-app-grid${featured ? ' exp-app-grid--featured' : ''}`}>
			{apps.map(app => (
				<ExperienceAppTile
					key={app.path}
					app={app}
					featured={featured}
					isFavorite={favorites?.has(app.path)}
					onNavigate={onNavigate}
					onToggleFavorite={onToggleFavorite}
				/>
			))}
		</div>
	);
}

export interface ExperienceLauncherSectionProps {
	title: string;
	variant?: 'favorites' | 'recent' | 'category';
	apps: AppTileData[];
	favorites?: Set<string>;
	maxVisible?: number;
	onNavigate?: (path: string) => void;
	onToggleFavorite?: (path: string) => void;
}

const DEFAULT_VISIBLE_ROWS = 2;
const DEFAULT_COLUMN_COUNT = 4;

function countGridColumns(templateValue: string): number {
	if (!templateValue || templateValue === 'none') {
		return 1;
	}

	let depth = 0;
	let tokenCount = 0;
	let tokenStart = false;

	for (const char of templateValue) {
		if (char === '(') {
			depth += 1;
		} else if (char === ')' && depth > 0) {
			depth -= 1;
		}

		if (char === ' ' && depth === 0) {
			if (tokenStart) {
				tokenCount += 1;
				tokenStart = false;
			}
		} else if (!tokenStart && char !== ' ') {
			tokenStart = true;
		}
	}

	if (tokenStart) {
		tokenCount += 1;
	}

	return Math.max(1, tokenCount);
}

function measureGridColumns(gridElement: HTMLDivElement): number {
	const gridTemplateColumns = window.getComputedStyle(gridElement).gridTemplateColumns;
	return countGridColumns(gridTemplateColumns);
}

export function ExperienceLauncherSection({
	title,
	variant,
	apps,
	favorites,
	maxVisible,
	onNavigate,
	onToggleFavorite,
}: ExperienceLauncherSectionProps) {
	const [expanded, setExpanded] = React.useState(false);
	const [columnCount, setColumnCount] = React.useState(DEFAULT_COLUMN_COUNT);
	const gridRef = React.useRef<HTMLDivElement>(null);
	const cutoff = Math.max(1, maxVisible ?? (columnCount * DEFAULT_VISIBLE_ROWS));
	const visible = expanded ? apps : apps.slice(0, cutoff);
	const hiddenCount = Math.max(0, apps.length - cutoff);
	const hasMore = hiddenCount > 0;
	const isFeatured = variant === 'favorites';

	React.useEffect(() => {
		if (maxVisible !== undefined) {
			return;
		}

		const gridElement = gridRef.current;
		if (!gridElement) {
			return;
		}

		const updateColumns = () => {
			setColumnCount(measureGridColumns(gridElement));
		};

		updateColumns();

		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const resizeObserver = new ResizeObserver(() => updateColumns());
		resizeObserver.observe(gridElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [maxVisible]);

	const headerIcon = variant === 'favorites'
		? <Star size={18} strokeWidth={2} aria-hidden />
		: variant === 'recent'
			? <Clock size={18} strokeWidth={1.5} aria-hidden />
			: null;

	return (
		<section
			className="exp-launcher-section"
			data-testid={`launcher-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
		>
			<h3 className={`exp-launcher-section__header${isFeatured ? ' exp-launcher-section__header--favorites' : ''}`}>
				{headerIcon}
				{title}
			</h3>
			<ExperienceAppGrid
				apps={visible}
				featured={isFeatured}
				favorites={favorites}
				gridRef={gridRef}
				onNavigate={onNavigate}
				onToggleFavorite={onToggleFavorite}
			/>
			{hasMore && (
				<button
					className="exp-section__toggle"
					onClick={() => setExpanded(v => !v)}
				>
					{expanded ? (
						<>Show less <ChevronUp size={14} strokeWidth={1.5} /></>
					) : (
						<>Show {hiddenCount} more <ChevronDown size={14} strokeWidth={1.5} /></>
					)}
				</button>
			)}
		</section>
	);
}
